// Helper to aggregate donut chart data based on metric type and orders data

type Order = { mission_advancement_id: number; count: number };
type rating = 'A' | 'B' | 'C' | 'D' | 'E' | 'S' | 99;

interface DonutAggregationResult {
  donutDataConsultant: Record<string, number>;
  donutDataDM: Record<string, number>;
  donutDataClient: Record<string, number>;
}

interface RatingDetail {
  rating: rating;
  count: number;
}

interface GraphDataOrders {
  consultant_submission?: Order[];
  dm_validation?: Order[];
  client_acceptance?: Order[];
  rating_details?: RatingDetail[];
}

// Status mapper function
function getStatus(id: number, context?: 'consultant' | 'dm' | 'client'): string {
  const statuses: { [key: number]: string } = {
    2: 'DM REFUSE',
    3: 'DM VALIDATED',
    4: 'DM ACCEPTED WITH RESERVATION',
    5: 'CLIENT REFUSE',
    6: 'CLIENT ACCEPTED WITH RESERVATION',
    7: 'CLIENT ACCEPTED',
    99: 'NOT SUBMITTED',
  };

  if (context === 'consultant') {
    return id === 1 ? 'SUBMITTED' : 'NOT SUBMITTED';
  }
  // 2. ID 1 specific context logic
  if (id === 1) {
    if (context === 'dm') return 'TO VALIDATE BY DM';
  }

  // 3. ID 99 specific context logic
  if (id === 99) {
    if (context === 'dm') return 'DM PENDING SUBMISSION';
    if (context === 'client') return 'CLIENT PENDING SUBMISSION';
  }

  // 4. Waiting Client Acceptance logic
  if ((id === 3 || id === 4) && context === 'client') {
    return 'WAITING CLIENT ACCEPTANCE';
  }

  return statuses[id] || '';
}

// Merge consultant data (combine ID 99 and 2)
function mergeConsultantData(list: Order[]): Order[] {
  if (!Array.isArray(list)) return [];

  let arr = [...list];
  const obj99 = arr.find((o) => o.mission_advancement_id === 99);
  const obj2 = arr.find((o) => o.mission_advancement_id === 2);

  if (obj99 && obj2) {
    const index99 = arr.findIndex((o) => o.mission_advancement_id === 99);
    if (index99 !== -1) {
      arr[index99] = { ...arr[index99], count: arr[index99].count + obj2.count };
    }
    arr = arr.filter((o) => o.mission_advancement_id !== 2);
  }
  return arr;
}

/**
 * Aggregate donut data for metric 1 (Workflow)
 */
function aggregateMetric1(orders: GraphDataOrders): DonutAggregationResult {
  const consultant = { SUBMITTED: 0, 'NOT SUBMITTED': 0 };
  const dm = {
    'DM REFUSE': 0,
    'TO VALIDATE BY DM': 0,
    'DM PENDING SUBMISSION': 0,
    'DM VALIDATED': 0,
    'DM ACCEPTED WITH RESERVATION': 0,
  };
  const client = {
    'CLIENT REFUSE': 0,
    'WAITING CLIENT ACCEPTANCE': 0,
    'CLIENT PENDING SUBMISSION': 0,
    'CLIENT ACCEPTED': 0,
    'CLIENT ACCEPTED WITH RESERVATION': 0,
  };

  // Merge consultant data first
  const mergedConsultant = mergeConsultantData(orders.consultant_submission || []);

  // Process consultant data
  mergedConsultant.forEach((order) => {
    const status = getStatus(order.mission_advancement_id, 'consultant');
    if (status in consultant) {
      consultant[status as keyof typeof consultant] += order.count;
    }
  });

  // Process DM and Client data
  (['dm_validation', 'client_acceptance'] as const).forEach((category) => {
    const categoryData = orders[category];
    if (Array.isArray(categoryData)) {
      categoryData.forEach((order) => {
        const context = category === 'dm_validation' ? 'dm' : 'client';
        const status = getStatus(order.mission_advancement_id, context);

        if (category === 'dm_validation' && status in dm) {
          dm[status as keyof typeof dm] += order.count;
        } else if (category === 'client_acceptance' && status in client) {
          client[status as keyof typeof client] += order.count;
        }
      });
    }
  });

  return {
    donutDataConsultant: consultant,
    donutDataDM: dm,
    donutDataClient: client,
  };
}

/**
 * Aggregate donut data for metric 2 (Acceptance)
 * Maps mission_advancement_id to acceptance states
 */
function aggregateMetric2(orders: GraphDataOrders): DonutAggregationResult {
  // For metric 2, we adapt the structure to acceptance states

  const client = {
    'CLIENT REFUSE': 0,
    'WAITING CLIENT ACCEPTANCE': 0,
    'CLIENT PENDING SUBMISSION': 0,
    'CLIENT ACCEPTED': 0,
    'CLIENT ACCEPTED WITH RESERVATION': 0,
  };

  // Process client acceptance data (primary data for metric 2)
  const clientData = orders.client_acceptance || [];
  clientData.forEach((order) => {
    switch (order.mission_advancement_id) {
      case 7: // Accepted
        client['CLIENT ACCEPTED'] += order.count;
        break;
      case 6: // Accepted with reservation
        client['CLIENT ACCEPTED WITH RESERVATION'] += order.count;
        break;
      case 5: // Rejected
        client['CLIENT REFUSE'] += order.count;
        break;
      case 3: // Pending
      case 4:
        client['WAITING CLIENT ACCEPTANCE'] += order.count;
        break;
      case 2:
      case 99:
        client['CLIENT PENDING SUBMISSION'] += order.count;
        break;
      default:
        break;
    }
  });

  return {
    donutDataConsultant: {},
    donutDataDM: {},
    donutDataClient: client,
  };
}

function aggregateMetric3(orders: GraphDataOrders): DonutAggregationResult {
  // For metric 3, aggregate by rating type
  const client: Record<string, number> = {
    A: 0,
    B: 0,
    C: 0,
    D: 0,
    E: 0,
    S: 0,
    'Not Rated': 0,
  };
  const clientData = orders.rating_details || [];

  clientData.forEach((order: { rating: rating; count: number }) => {
    if (order.rating === 99) {
      client['Not Rated'] += order.count;
    } else if (['A', 'B', 'C', 'D', 'E', 'S'].includes(order.rating as string)) {
      client[order.rating as string] += order.count;
    }
  });
  // console.log(client);

  return {
    donutDataConsultant: {},
    donutDataDM: {},
    donutDataClient: client,
  };
}

/**
 * Main aggregation function that routes to metric-specific aggregators
 */
export default function aggregateDonutData(orders: GraphDataOrders, metricId = 1): DonutAggregationResult {
  if (metricId === 2) {
    return aggregateMetric2(orders);
  }
  if (metricId === 3) {
    return aggregateMetric3(orders);
  }
  // Default to metric 1
  return aggregateMetric1(orders);
}
